import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { trpc } from '@client/trpc/client';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/claims/catastrophe-events')({
    staticData: {
        metadata: {
            title: 'Catastrophe events',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <CatastropheEventsSearch />
        </PageController>
    );
}

type QueryParams = RouterInputs['catastropheEvent']['listCatastropheEvents'];

const columnHelper = createColumnHelper<
    NonNullable<RouterOutputs['catastropheEvent']['listCatastropheEvents']>['rows'][number],
    NonNullable<QueryParams['orderBy']>
>();

function CatastropheEventsSearch() {
    const orgId = useOrgId();
    const { trpcUtils } = useTrpc();
    const { mutate } = trpc.catastropheEvent.deleteCatastropheEvent.useMutation();
    const { formatDate } = useFormatter();
    const navigate = useNavigate();

    const [input, setInput] = useState<QueryParams>({});

    const { data, isPending, error, refetch, isFetching } = trpc.catastropheEvent.listCatastropheEvents.useQuery(input);

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: data?.rows,
        total: data?.total,
        loading: isPending,
        queryParams: input,
        onParamsChange: setInput,
        // Filters, sort and page survive a refresh (CUR-1341).
        urlKey: 'events',
        queryError: error,
        emptyState: { noRecordsTitle: 'No catastrophe events yet' },
        columns: [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                sortable: 'name',
            }),
            columnHelper.accessor('startDate', {
                id: 'startDate',
                header: 'Start date',
                sortable: 'startDate',
                cell({ row }) {
                    return formatDate(row.startDate);
                },
            }),
            columnHelper.accessor('endDate', {
                id: 'endDate',
                header: 'End date',
                sortable: 'endDate',
                cell({ row }) {
                    return formatDate(row.endDate);
                },
            }),
            columnHelper.actions({
                cell({ row }) {
                    return (
                        <Button.Delete
                            onConfirm={(onSuccess) =>
                                mutate(
                                    { id: row.id },
                                    {
                                        onSuccess() {
                                            onSuccess();
                                            void trpcUtils.catastropheEvent.invalidate();
                                        },
                                        onError(error) {
                                            errorMessage.show(error);
                                        },
                                    },
                                )
                            }
                        />
                    );
                },
            }),
        ],
    });

    if (error) {
        return <InlineQueryError error={error} onRetry={() => void refetch()} retrying={isFetching} />;
    }

    return (
        <>
            <Outlet />
            <TableToolbar
                variant="enhanced"
                actions={
                    <Button.Add.Link
                        to="/console/$orgId/configuration/claims-products/claims/catastrophe-events/add"
                        params={{
                            orgId,
                        }}
                        variant="primary"
                        label="New catastrophe event"
                    />
                }
            />
            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/configuration/claims-products/claims/catastrophe-events/edit/$catastropheEventId',
                                params: {
                                    orgId,
                                    catastropheEventId: id,
                                },
                            },
                            e,
                        );
                    },
                })}
            />
        </>
    );
}
